/**
 * Wave 12.4 — "Instalar no celular" banner.
 *
 * Appears once the browser has flagged the PWA as installable AND we
 * haven't been dismissed this session AND we aren't already running
 * standalone. On tap we fire `prompt()`, which hands off to the
 * browser's own install flow — we never fake it.
 *
 * Emil-design notes:
 *   - Enters from the bottom (translateY(100%) → 0) with the stronger
 *     drawer curve over 260ms. The element's own height is unknown, so
 *     percentage-based translation saves us a ResizeObserver.
 *   - Close button uses `pressable` for the scale(0.97) press-state.
 *   - Never shown to owners or brokers — they're on a single-project
 *     portal and the shortcut surface would be noise.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/store/auth.store';
import { useInstallPrompt } from '@/lib/pwa';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

/**
 * W124.6 — rotas onde o prompt PWA NÃO deve aparecer.
 *
 * Critério: superfícies críticas onde o card flutuante atrapalha o fluxo
 * primário. JP reportou 2026-05-26 que o card cobria o CTA do wizard
 * "Criar projeto" no mobile (z-index fix shipou em W124.1, mas o prompt
 * em si ainda é ruído visual quando o user está focado em criar obra ou
 * aprovar orçamento).
 *
 *   - `/maintenance`           — banner já ocupa o viewport
 *   - `/budget-approval/*`     — link público, single-shot decision
 *   - `/o/*`                   — owner portal, single project
 *   - `/projects/novo`         — wizard de criação (cobre CTA)
 */
const SUPPRESSED_ROUTE_PREFIXES = [
  '/maintenance',
  '/budget-approval',
  '/o/',
  '/projects/novo',
];

function isSuppressedRoute(pathname: string): boolean {
  return SUPPRESSED_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + '/') || pathname.startsWith(p),
  );
}

/**
 * Wave 332 · gate por viewport: o prompt "instalar" só faz sentido em
 * mobile/tablet com touch. No desktop o navegador já oferece o ícone de
 * instalar na barra; nosso banner virava ruído (JP reportou 2026-04-29).
 * Critério: ponteiro grosso (touch) OU largura ≤ 768 px. Re-avalia em
 * resize porque um tablet em landscape pode atravessar o limite.
 */
function useIsHandheld(): boolean {
  const [handheld, setHandheld] = useState(() => {
    if (typeof window === 'undefined') return false;
    const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
    const narrow = window.matchMedia?.('(max-width: 768px)').matches ?? false;
    return coarse || narrow;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const coarseMq = window.matchMedia('(pointer: coarse)');
    const narrowMq = window.matchMedia('(max-width: 768px)');
    const update = () => setHandheld(coarseMq.matches || narrowMq.matches);
    coarseMq.addEventListener?.('change', update);
    narrowMq.addEventListener?.('change', update);
    return () => {
      coarseMq.removeEventListener?.('change', update);
      narrowMq.removeEventListener?.('change', update);
    };
  }, []);
  return handheld;
}

export function InstallPrompt() {
  const { t } = useT();
  const user = useAuth((s) => s.user);
  const handheld = useIsHandheld();
  const location = useLocation();
  const { canInstall, isStandalone, prompt, dismissed, dismiss } =
    useInstallPrompt();

  if (!user) return null;
  if (user.role === 'client' || user.role === 'broker') return null;
  if (!handheld) return null;
  if (!canInstall || isStandalone || dismissed) return null;
  if (isSuppressedRoute(location.pathname)) return null;

  const onInstall = async () => {
    const outcome = await prompt();
    if (outcome === 'dismissed' || outcome === 'unavailable') {
      dismiss();
    }
  };

  // Wave 89 QA polish · evita cobrir o MobileBottomNav (64px + safe-area-inset-bottom)
  // em mobile. No desktop (sem bottom nav) usa só a safe-area-inset-bottom.
  // Antes: bottom-0 + pb-3 deixava o card ~88px de altura sobreposto às
  // tabs Hoje/RDO+/Galeria/Inbox/Mais — PM não conseguia clicar nas tabs
  // até dismissar. Agora o card senta acima do bottom nav e respeita a
  // safe area do iOS (notch + indicador home).
  return (
    <div
      role="dialog"
      aria-live="polite"
      className={cn(
        'fixed inset-x-0 z-40 flex justify-center px-3',
        // mobile: 64px (MobileBottomNav) + 12px gap + safe-area
        // desktop (md+): só safe-area + 12px gap
        'bottom-[calc(64px+env(safe-area-inset-bottom)+12px)]',
        'md:bottom-[calc(env(safe-area-inset-bottom)+12px)]',
        'pointer-events-none',
      )}
    >
      <div
        className={cn(
          'pointer-events-auto w-full max-w-md',
          'rounded-2xl border border-line bg-surface-2 shadow-pop',
          'dark:shadow-pop-dark px-4 py-3',
          'animate-drawer-in',
        )}
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-brand-600 p-1.5">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M3 17V9l7-5 7 5v8H3z"
                stroke="#F5F5F0"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ink">
              {t.wave12.pwa.installTitle}
            </p>
            <p className="mt-0.5 text-xs text-ink-muted">
              {t.wave12.pwa.installBody}
            </p>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={onInstall}
                className={cn(
                  'pressable min-h-[44px] rounded-lg bg-brand-600 px-3 py-1.5',
                  'text-xs font-semibold text-white hover:bg-brand-500',
                  'transition-colors duration-150',
                )}
              >
                {t.wave12.pwa.installCta}
              </button>
              <button
                type="button"
                onClick={dismiss}
                className={cn(
                  'pressable min-h-[44px] rounded-lg border border-line bg-surface px-3 py-1.5',
                  'text-xs font-medium text-ink-muted hover:text-ink hover:bg-surface-3',
                  'transition-colors duration-150',
                )}
              >
                {t.wave12.pwa.later}
              </button>
            </div>
          </div>
          {/*
           * W124.6 · botão X explícito no canto sup-direito. Antes só
           * "Agora não" — alguns PMs reportaram não enxergar o secondary
           * (texto pequeno, sem affordance de fechar). O X dá saída clara
           * sem alterar a política: ele chama o mesmo `dismiss()` (7d TTL).
           */}
          <button
            type="button"
            onClick={dismiss}
            aria-label={t.common.close}
            className={cn(
              'pressable -mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center',
              'rounded-md text-ink-muted hover:bg-surface-3 hover:text-ink',
              'transition-colors duration-150',
            )}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
              className="h-4 w-4"
            >
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
